import type { BulkUploadRepository } from "../../domain/repositories/BulkUploadRepository";
import type {
  BulkUploadIssueRowEntity,
  BulkUploadInsertedRecordEntity,
  BulkUploadCreatedRecordEntity,
  BulkUploadResultEntity,
} from "../../domain/entities/BulkUploadEntity";
import type {
  BulkUploadApiResponseDto,
  BulkUploadInsertedRecordDto,
  BulkUploadIssueRowDto,
  BulkUploadResultDataDto,
} from "../dto/BulkUploadDto";
import {
  downloadCustomersTemplate,
  downloadVendorsTemplate,
  uploadCustomersFile,
  uploadVendorsFile,
  verifyCustomersBulkForReview,
  verifyVendorsBulkForReview,
} from "../api/bulkUploadApi";
import {
  downloadGlsTemplate,
  uploadGlsFile,
  verifyGlsBulkForReview,
} from "../../../gls/data/api/glBulkUploadApi";

function mapIssueRow(dto: BulkUploadIssueRowDto): BulkUploadIssueRowEntity {
  const st = dto.status ?? "";
  const allowed: BulkUploadIssueRowEntity["status"][] = [
    "validation_failed",
    "already_exists",
    "zoho_failed",
    "db_failed",
  ];
  const status =
    typeof st === "string" &&
    (allowed as readonly string[]).includes(st)
      ? (st as NonNullable<BulkUploadIssueRowEntity["status"]>)
      : undefined;
  return {
    row: dto.row,
    field: dto.field,
    message: dto.message,
    displayName: dto.display_name,
    status,
  };
}

function mapInsertedRecordRow(
  r: BulkUploadInsertedRecordDto,
  mode: "customers" | "vendors" | "gls",
): BulkUploadInsertedRecordEntity {
  const displayName =
    mode === "customers"
      ? r.customer_name ?? ""
      : mode === "vendors"
        ? r.vendor_legal_name ?? ""
        : (r as BulkUploadInsertedRecordDto & { account_name?: string })
            .account_name ?? "";
  const entityId =
    mode === "customers"
      ? r.customer_id
      : mode === "vendors"
        ? r.vendor_id
        : undefined;

  return {
    row: r.row,
    displayName,
    entityId,
  };
}

function mapCreatedRecords(
  d: BulkUploadResultDataDto,
  mode: "customers" | "vendors" | "gls",
): BulkUploadCreatedRecordEntity[] {
  const fromApi = d.created_records ?? [];
  if (fromApi.length > 0) {
    return fromApi.map((r) => ({
      row: r.row,
      displayName: r.display_name ?? "",
      entityId: r.entity_id ?? undefined,
    }));
  }

  return (d.inserted_records ?? []).map((r) =>
    mapInsertedRecordRow(r, mode),
  );
}

function mapIssueList(
  list: BulkUploadIssueRowDto[] | undefined,
): BulkUploadIssueRowEntity[] {
  return (list ?? []).map(mapIssueRow);
}

/** Insert-time failures that are neither "fix in Excel" nor "already exists" — Zoho Books + DB. */
const OTHER_BUCKET_FIELDS = new Set([
  "zoho_creation",
  "gstin",
  "state",
  "pan_tax_id",
  "pan_tax_registration_no",
  "insert",
  "currency",
  "embedding",
  "tds_tax_rate",
]);

function heuristicKind(e: BulkUploadIssueRowEntity): "skipped" | "validation" | "other" {
  if (/\balready exists\b/i.test(e.message)) {
    return "skipped";
  }
  if (e.field.toLowerCase() === "duplicate") {
    return "validation";
  }
  if (OTHER_BUCKET_FIELDS.has(e.field)) {
    return "other";
  }
  return "validation";
}

function partitionMergedErrors(errors: BulkUploadIssueRowEntity[]): {
  validationFailures: BulkUploadIssueRowEntity[];
  skippedExisting: BulkUploadIssueRowEntity[];
  otherFailures: BulkUploadIssueRowEntity[];
} {
  if (errors.length === 0) {
    return {
      validationFailures: [],
      skippedExisting: [],
      otherFailures: [],
    };
  }

  const hasStatus = errors.some((entry) => Boolean(entry.status));

  const validationFailures: BulkUploadIssueRowEntity[] = [];
  const skippedExisting: BulkUploadIssueRowEntity[] = [];
  const otherFailures: BulkUploadIssueRowEntity[] = [];

  const pushByHeuristic = (entity: BulkUploadIssueRowEntity) => {
    switch (heuristicKind(entity)) {
      case "skipped":
        skippedExisting.push(entity);
        break;
      case "other":
        otherFailures.push(entity);
        break;
      default:
        validationFailures.push(entity);
    }
  };

  if (hasStatus) {
    for (const entity of errors) {
      if (entity.status === "already_exists") {
        skippedExisting.push(entity);
      } else if (
        entity.status === "zoho_failed" ||
        entity.status === "db_failed"
      ) {
        otherFailures.push(entity);
      } else if (entity.status === "validation_failed") {
        validationFailures.push(entity);
      } else {
        pushByHeuristic(entity);
      }
    }
  } else {
    for (const entity of errors) {
      pushByHeuristic(entity);
    }
  }

  return { validationFailures, skippedExisting, otherFailures };
}

function mapResponseToEntity(
  dto: BulkUploadApiResponseDto,
  mode: "customers" | "vendors" | "gls",
): BulkUploadResultEntity {
  const d = dto.data;

  const zMapped = mapIssueList(d.zoho_failures);
  const bMapped = mapIssueList(d.db_failures);

  let validationFailures = mapIssueList(d.validation_failures);
  let skippedExisting = mapIssueList(d.skipped_existing);
  let otherFailures: BulkUploadIssueRowEntity[] = [...zMapped, ...bMapped];

  const errors = (d.errors ?? []).map(mapIssueRow);

  const needsPartition =
    validationFailures.length === 0 &&
    skippedExisting.length === 0 &&
    zMapped.length === 0 &&
    bMapped.length === 0 &&
    errors.length > 0;

  if (needsPartition) {
    const part = partitionMergedErrors(errors);
    validationFailures = part.validationFailures;
    skippedExisting = part.skippedExisting;
    otherFailures = part.otherFailures;
  }

  const otherFailedCount = needsPartition
    ? otherFailures.length
    : (d.zoho_failed_count ?? zMapped.length) + (d.db_failed_count ?? bMapped.length);

  return {
    totalRows: d.total_rows,
    processedRows: d.processed_rows,
    validatedSuccessful: d.validated_successful,
    validatedFailed: d.validated_failed,
    validationFailedCount: d.validation_failed_count ?? d.validated_failed,
    alreadyExistsCount: d.already_exists_count ?? skippedExisting.length,
    otherFailedCount,
    insertedCount: d.inserted_count,
    insertFailedCount: d.insert_failed_count,
    createdCount: d.created_count ?? d.inserted_count,
    createdRecords: mapCreatedRecords(d, mode),
    insertedRecords: (d.inserted_records ?? []).map((r) =>
      mapInsertedRecordRow(r, mode),
    ),
    validationFailures,
    skippedExisting,
    otherFailures,
    errors,
  };
}

export class BulkUploadRepositoryImpl implements BulkUploadRepository {
  async downloadCustomerTemplate(): Promise<void> {
    await downloadCustomersTemplate();
  }

  async downloadVendorTemplate(): Promise<void> {
    await downloadVendorsTemplate();
  }

  async downloadGlTemplate(): Promise<void> {
    await downloadGlsTemplate();
  }

  async uploadCustomers(file: File): Promise<BulkUploadResultEntity> {
    const dto = await uploadCustomersFile(file);
    return mapResponseToEntity(dto, "customers");
  }

  async uploadVendors(file: File): Promise<BulkUploadResultEntity> {
    const dto = await uploadVendorsFile(file);
    return mapResponseToEntity(dto, "vendors");
  }

  async uploadGls(file: File): Promise<BulkUploadResultEntity> {
    const dto = await uploadGlsFile(file);
    return mapResponseToEntity(dto, "gls");
  }

  async verifyCustomersForReview(file: File) {
    return verifyCustomersBulkForReview(file);
  }

  async verifyVendorsForReview(file: File) {
    return verifyVendorsBulkForReview(file);
  }

  async verifyGlsForReview(file: File) {
    return verifyGlsBulkForReview(file);
  }
}
